import { Schema } from "effect";

export const INTERNAL_REQUEST_CLOCK_SKEW_MS = 60_000;
export const INTERNAL_REQUEST_MAX_BODY_BYTES = 512 * 1_024;
export const DEFAULT_INTERNAL_REQUEST_KEY_ID = "papersflow-internal-v1";

export const InternalRequestPathSchema = Schema.Literals([
  "/internal/agent-runs/begin",
  "/internal/agent-runs/attachment",
  "/internal/agent-runs/events",
  "/internal/agent-runs/complete",
  "/internal/agent-runs/acquire-lease",
  "/internal/agent-runs/renew-lease",
  "/internal/agent-runs/authorize-browser-tool",
  "/internal/agent-runs/release-lease",
  "/internal/agent-runs/request-cancellation",
  "/internal/agent-runs/commit-attempt",
  "/internal/agent-runs/accept-delegation",
  "/internal/agent-runs/suspend-for-child",
  "/internal/agent-runs/claim-next-attempt",
  "/internal/agent-runs/commit-child-result",
  "/internal/agent-model-calls/claim",
  "/internal/agent-model-calls/start",
  "/internal/agent-model-calls/settle",
  "/internal/agent-model-calls/outcome-unknown",
  "/internal/research/prepare",
  "/internal/research/claim-submission",
  "/internal/research/begin-submission",
  "/internal/research/mark-submission-outcome",
  "/internal/research/release-submission-claim",
  "/internal/research/attach-provider",
  "/internal/research/claim-provider-activity-observer",
  "/internal/research/renew-provider-activity-observer",
  "/internal/research/release-provider-activity-observer",
  "/internal/research/replace-provider-activity",
  "/internal/research/observe-terminal",
  "/internal/research/recovery-context",
  "/internal/research/recoverable",
  "/internal/research/reconcile-costs",
  "/internal/research/claim-finalization",
  "/internal/research/release-finalization",
  "/internal/research/reserve-finalization-objects",
  "/internal/research/commit-finalization",
  "/internal/research/fail-finalization",
  "/internal/research/claim-storage-cleanup",
  "/internal/research/record-storage-cleanup",
  "/internal/research/authorize-report-read",
  "/internal/research/cancel",
  "/internal/media/resolve-actor",
  "/internal/media/prepare-upload",
  "/internal/media/attach-upload",
  "/internal/media/mark-uploaded",
  "/internal/media/resolve-upload",
  "/internal/media/get",
  "/internal/media/resolve-playback",
  "/internal/media/begin-caption",
  "/internal/media/attach-caption",
  "/internal/media/cancel-caption",
  "/internal/media/finalize-caption",
  "/internal/media/cancel",
  "/internal/media/claim-cleanup",
  "/internal/media/record-cleanup",
  "/internal/media/recoverable-processing",
  "/internal/media/claim-processing",
  "/internal/media/record-probe",
  "/internal/media/record-progress",
  "/internal/media/renew-processing",
  "/internal/media/reserve-processing-outputs",
  "/internal/media/release-processing-attempt",
  "/internal/media/complete-processing",
  "/internal/media/fail-processing",
  "/internal/connectors/resolve-actor",
  "/internal/connectors/resolve-session",
  "/internal/connectors/reconcile-state",
  "/internal/calendar-operations/claim",
  "/internal/calendar-operations/claim-reconciliation",
  "/internal/calendar-operations/complete",
  "/internal/calendar-operations/fail",
  "/internal/calendar-operations/reconcile",
  "/internal/calendar-sync/claim",
  "/internal/calendar-sync/complete",
  "/internal/calendar-sync/fail",
  "/internal/calendar-tools/search",
  "/internal/calendar-tools/get",
  "/internal/calendar-tools/propose",
  "/internal/project-files/resolve-actor",
  "/internal/project-files/prepare-upload",
  "/internal/project-files/resolve-upload",
  "/internal/project-files/finalize-upload",
  "/internal/project-files/cancel-upload",
  "/internal/project-files/delete-file",
  "/internal/project-files/resolve-download",
  "/internal/project-files/claim-cleanup",
  "/internal/project-files/record-cleanup-result",
  "/internal/project-tasks/list",
  "/internal/project-tasks/propose",
  "/internal/office/resolve-revision",
  "/internal/office/begin-patch",
  "/internal/office/get-patch",
  "/internal/office/cancel-patch",
  "/internal/office/claim-patch",
  "/internal/office/renew-patch",
  "/internal/office/complete-patch",
  "/internal/office/fail-patch",
  "/internal/office/authorize-patch-cleanup",
  "/internal/office/record-patch-cleanup",
  "/internal/office/begin-creation",
  "/internal/office/get-creation",
  "/internal/office/cancel-creation",
  "/internal/office/begin-project-file-import",
  "/internal/office/get-project-file-import",
  "/internal/office/cancel-project-file-import",
  "/internal/office/begin-pdf-to-docx",
  "/internal/office/get-pdf-to-docx",
  "/internal/office/cancel-pdf-to-docx",
  "/internal/office/claim-creation",
  "/internal/office/renew-creation",
  "/internal/office/release-creation-attempt",
  "/internal/office/recover-creation-attempt",
  "/internal/office/complete-creation",
  "/internal/office/fail-creation",
  "/internal/office/authorize-creation-cleanup",
  "/internal/office/record-creation-cleanup",
  "/internal/images/prepare",
  "/internal/images/probe-runtime",
  "/internal/images/begin",
  "/internal/images/observe-provider-generation",
  "/internal/images/save",
  "/internal/images/finalize",
  "/internal/images/fail",
  "/internal/images/claim-cost-reconciliation",
  "/internal/images/record-cost-reconciliation-result",
  "/internal/images/resolve-revision",
  "/internal/images/claim-cleanup",
  "/internal/images/record-cleanup-result",
  "/internal/sandboxes/operations/prepare",
  "/internal/sandboxes/operations/begin-ensure",
  "/internal/sandboxes/operations/begin-existing",
  "/internal/sandboxes/operations/complete",
  "/internal/sandboxes/files/discover",
  "/internal/sandboxes/files/authorize",
  "/internal/polar-events",
]);

const LowerHexSha256Schema = Schema.String.check(
  Schema.makeFilter(
    (value) => /^[a-f0-9]{64}$/.test(value) || "must be a lowercase SHA-256 hexadecimal value",
  ),
);

const UrlSafeIdentifierSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(80),
  Schema.makeFilter(
    (value) => /^[a-zA-Z0-9_-]+$/.test(value) || "must contain only URL-safe characters",
  ),
);

export const InternalRequestSignatureInputSchema = Schema.Struct({
  method: Schema.Literal("POST"),
  path: InternalRequestPathSchema,
  keyId: UrlSafeIdentifierSchema,
  timestamp: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  nonce: Schema.String.check(
    Schema.isMinLength(16),
    Schema.isMaxLength(128),
    Schema.makeFilter(
      (value) => /^[a-zA-Z0-9_-]+$/.test(value) || "must contain only URL-safe characters",
    ),
  ),
  bodySha256: LowerHexSha256Schema,
});

export type InternalRequestPath = typeof InternalRequestPathSchema.Type;
export type InternalRequestSignatureInput = typeof InternalRequestSignatureInputSchema.Type;

export const decodeInternalRequestSignatureInput = Schema.decodeUnknownSync(
  InternalRequestSignatureInputSchema,
  {
    errors: "all",
    onExcessProperty: "error",
    reportInput: false,
  },
);

export function formatInternalRequestSignaturePayload(
  input: InternalRequestSignatureInput,
): string {
  return [
    input.method,
    input.path,
    input.keyId,
    String(input.timestamp),
    input.nonce,
    input.bodySha256,
  ].join("\n");
}
